import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  CalendarDays,
  Video,
  MapPin,
  CheckCircle,
  XCircle,
  Award,
  BookOpen,
  Layers,
  ExternalLink,
} from 'lucide-react';
import { memberApiFetch, isAuthenticated } from '@/lib/memberAuth';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';

interface ScheduleItem {
  event_id: number;
  series_id: string | null;
  series_title: string | null;
  session_title: string;
  start_time: string | null;
  end_time: string | null;
  location_type: string | null;
  meeting_link: string | null;
  status: string | null;
  response_status: string | null;
  attended: boolean | null;
}
interface CurriculumItem {
  curriculum_id: number;
  curriculum_name: string;
  status: string;
  progress_percentage: number;
  final_exam_passed: boolean;
  lms_course_id: number | null;
}
interface CertItem {
  certification_id: number;
  name: string;
  status: string;
  certificate_number: string | null;
  certification_date: string | null;
  expiration_date: string | null;
}
interface Overview {
  lms_enabled: boolean;
  schedule: ScheduleItem[];
  curricula: CurriculumItem[];
  certifications: CertItem[];
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return 'TBD';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'TBD';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const RESPONSE_STYLES: Record<string, string> = {
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  tentative: 'bg-amber-100 text-amber-700',
  pending: 'bg-gray-100 text-gray-600',
};

export default function MemberTrainingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gated, setGated] = useState(false);
  const [respondingId, setRespondingId] = useState<number | null>(null);
  const [viewingCert, setViewingCert] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await memberApiFetch('/training/overview');
      if (res.status === 403) {
        setGated(true);
        return;
      }
      if (!res.ok) throw new Error('Failed to load your training');
      setData(await res.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load your training');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login', { replace: true });
      return;
    }
    load();
  }, [navigate, load]);

  const respond = async (item: ScheduleItem, status: 'accepted' | 'declined') => {
    setRespondingId(item.event_id);
    try {
      // Respond to the whole series when the session belongs to one, else the
      // single session.
      const body = item.series_id
        ? { series_id: item.series_id, response_status: status }
        : { event_id: item.event_id, response_status: status };
      const res = await memberApiFetch('/training/respond', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.ok) await load();
    } catch {
      /* surfaced on next load */
    } finally {
      setRespondingId(null);
    }
  };

  const viewCertificate = async (cert: CertItem) => {
    setViewingCert(cert.certification_id);
    try {
      const res = await memberApiFetch(
        `/training/certifications/${cert.certification_id}/certificate`,
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Could not open the certificate. Please try again.');
    } finally {
      setViewingCert(null);
    }
  };

  const header = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </Link>
      }
      title="My Training"
      description="Your schedule, courses, and certifications"
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <PageContainer size="narrow">
          <PageState variant="loading" message="Loading your training…" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {header}
      <PageContainer size="narrow">
        {gated && (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">
              Training isn’t enabled for your organization yet.
            </CardContent>
          </Card>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 text-red-700 text-sm p-3">{error}</div>
        )}

        {data && !gated && (
          <>
            {/* Quick links: courses + exams */}
            <div className="grid grid-cols-2 gap-3">
              <Link to="/member/courses">
                <Card className="hover:border-emerald-300 transition-colors">
                  <CardContent className="flex items-center gap-3 py-4">
                    <BookOpen className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Courses & Exams</p>
                      <p className="text-xs text-gray-500">Join classes and take assessments</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Card>
                <CardContent className="flex items-center gap-3 py-4">
                  <Award className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {data.certifications.filter((c) => c.status === 'certified').length} Certification
                      {data.certifications.filter((c) => c.status === 'certified').length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-500">Earned credentials</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Schedule */}
            <section>
              <h2 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-emerald-600" /> Schedule
              </h2>
              {data.schedule.length === 0 ? (
                <Card>
                  <CardContent className="py-6 text-center text-sm text-gray-500">
                    You have no scheduled training sessions yet.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {data.schedule.map((s) => {
                    const resp = s.response_status || 'pending';
                    const isVirtual = s.location_type === 'virtual' || s.location_type === 'hybrid';
                    return (
                      <Card key={s.event_id}>
                        <CardContent className="py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">
                                {s.session_title}
                              </p>
                              {s.series_title && (
                                <p className="text-xs text-gray-400 truncate">{s.series_title}</p>
                              )}
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <CalendarDays className="h-3 w-3" />
                                  {fmtDate(s.start_time)}
                                </span>
                                {s.location_type && (
                                  <span className="flex items-center gap-1">
                                    {isVirtual ? (
                                      <Video className="h-3 w-3" />
                                    ) : (
                                      <MapPin className="h-3 w-3" />
                                    )}
                                    {s.location_type.replace('_', ' ')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge className={RESPONSE_STYLES[resp] || RESPONSE_STYLES.pending}>
                              {resp}
                            </Badge>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {resp !== 'accepted' && (
                              <Button
                                size="sm"
                                className="gap-1 bg-v2accent hover:bg-v2accent/90"
                                disabled={respondingId === s.event_id}
                                onClick={() => respond(s, 'accepted')}
                              >
                                <CheckCircle className="h-4 w-4" /> Accept
                              </Button>
                            )}
                            {resp !== 'declined' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                disabled={respondingId === s.event_id}
                                onClick={() => respond(s, 'declined')}
                              >
                                <XCircle className="h-4 w-4" /> Decline
                              </Button>
                            )}
                            {s.meeting_link && (
                              <a href={s.meeting_link} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" variant="outline" className="gap-1">
                                  <Video className="h-4 w-4" /> Join class
                                </Button>
                              </a>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Curricula */}
            {data.curricula.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Layers className="h-4 w-4 text-emerald-600" /> Curriculum progress
                </h2>
                <div className="space-y-2">
                  {data.curricula.map((c) => (
                    <Card key={c.curriculum_id}>
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 text-sm">{c.curriculum_name}</p>
                          <Badge
                            className={
                              c.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-gray-100 text-gray-600'
                            }
                          >
                            {c.status}
                          </Badge>
                        </div>
                        <Progress
                          value={Math.max(0, Math.min(100, c.progress_percentage || 0))}
                          className="mt-2 h-2"
                        />
                        <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {Math.round(c.progress_percentage || 0)}% complete
                            {c.final_exam_passed ? ' · exam passed' : ''}
                          </span>
                          {c.lms_course_id && (
                            <Link
                              to={`/member/courses/${c.lms_course_id}`}
                              className="text-emerald-600 hover:underline flex items-center gap-1"
                            >
                              Continue <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Certifications */}
            {data.certifications.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-600" /> Certifications
                </h2>
                <div className="space-y-2">
                  {data.certifications.map((cert) => {
                    const expired =
                      cert.expiration_date != null &&
                      new Date(cert.expiration_date).getTime() < Date.now();
                    return (
                      <Card key={cert.certification_id}>
                        <CardContent className="py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{cert.name}</p>
                            <p className="text-xs text-gray-500">
                              {cert.certificate_number ? `No. ${cert.certificate_number}` : ''}
                              {cert.certification_date
                                ? `${cert.certificate_number ? ' · ' : ''}Issued ${new Date(
                                    cert.certification_date,
                                  ).toLocaleDateString()}`
                                : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge
                              className={
                                expired
                                  ? 'bg-red-100 text-red-700'
                                  : cert.status === 'certified'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-gray-100 text-gray-600'
                              }
                            >
                              {expired ? 'expired' : cert.status}
                            </Badge>
                            {cert.status === 'certified' && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={viewingCert === cert.certification_id}
                                onClick={() => viewCertificate(cert)}
                              >
                                View
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </PageContainer>
    </>
  );
}
